import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaUser, FaEnvelope, FaTelegram, FaCoins, FaShieldAlt, FaEdit, FaSave, FaTimes, FaCheckCircle, FaExclamationCircle, FaPaperPlane, FaUnlink, FaCopy } from 'react-icons/fa';
import { SiKick } from 'react-icons/si';

const ProfilePage = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditingTelegram, setIsEditingTelegram] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState('');
  const [updating, setUpdating] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [kickUsername, setKickUsername] = useState(null);
  const [kickLinkCode, setKickLinkCode] = useState(null);
  const [generatingKickCode, setGeneratingKickCode] = useState(false);
  const [unlinkingKick, setUnlinkingKick] = useState(false);
  const [kickCodeCopied, setKickCodeCopied] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('userToken');
    const storedUserData = localStorage.getItem('userData');

    if (!token || !storedUserData) {
      navigate('/login');
      return;
    }

    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('userToken');
        const response = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const user = response.data.user || response.data;
        setUserData(user);
        setTelegramUsername(user.telegramUsername || '');
        setEmailVerified(user.emailVerified || user.email_verified || false);
        setKickUsername(user.kickUsername || user.kick_username || null);
        
        // Update localStorage
        localStorage.setItem('userData', JSON.stringify(user));
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Fallback to stored data
        try {
          const parsedUser = JSON.parse(storedUserData);
          setUserData(parsedUser);
          setTelegramUsername(parsedUser.telegramUsername || '');
        } catch (e) {
          console.error('Error parsing user data:', e);
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleResendVerification = async () => {
    try {
      setResendingEmail(true);
      setEmailMessage('');
      const token = localStorage.getItem('userToken');
      await axios.post('/api/auth/resend-verification', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailMessage('success');
      setTimeout(() => {
        setEmailMessage('');
      }, 5000);
    } catch (error) {
      setEmailMessage('error');
      setTimeout(() => {
        setEmailMessage('');
      }, 5000);
    } finally {
      setResendingEmail(false);
    }
  };

  const getRoleDisplay = (role) => {
    switch (role) {
      case 'admin':
        return { text: 'Admin', color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500' };
      case 'moderator':
        return { text: 'Moderatör', color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500' };
      default:
        return { text: 'Kullanıcı', color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500' };
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userData');
    window.dispatchEvent(new Event('userTokenChanged'));
    navigate('/');
  };

  const handleUpdateTelegramUsername = async () => {
    if (!telegramUsername.trim()) {
      alert('Telegram kullanıcı adı boş olamaz');
      return;
    }

    setUpdating(true);
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.put('/api/users/me/telegram-username', 
        { telegramUsername },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUserData(response.data.user);
      setTelegramUsername(response.data.user.telegramUsername);
      setIsEditingTelegram(false);
      
      // Update localStorage
      localStorage.setItem('userData', JSON.stringify(response.data.user));
      
      alert('Telegram kullanıcı adı başarıyla güncellendi');
    } catch (error) {
      console.error('Error updating telegram username:', error);
      alert(error.response?.data?.error || 'Telegram kullanıcı adı güncellenirken bir hata oluştu');
    } finally {
      setUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingTelegram(false);
    setTelegramUsername(userData?.telegramUsername || '');
  };

  const handleGenerateKickCode = async () => {
    setGeneratingKickCode(true);
    try {
      const token = localStorage.getItem('userToken');
      const response = await axios.post('/api/users/me/kick/link-code', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKickLinkCode(response.data);
    } catch (error) {
      alert(error.response?.data?.error || 'Bağlantı kodu oluşturulamadı');
    } finally {
      setGeneratingKickCode(false);
    }
  };

  const handleCopyKickCommand = () => {
    if (!kickLinkCode) return;
    navigator.clipboard?.writeText(kickLinkCode.command);
    setKickCodeCopied(true);
    setTimeout(() => setKickCodeCopied(false), 2000);
  };

  const handleUnlinkKick = async () => {
    if (!window.confirm('Kick hesabı bağlantısını kaldırmak istediğinize emin misiniz?')) return;
    setUnlinkingKick(true);
    try {
      const token = localStorage.getItem('userToken');
      await axios.delete('/api/users/me/kick', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKickUsername(null);
      setKickLinkCode(null);
    } catch (error) {
      alert(error.response?.data?.error || 'Bağlantı kaldırılamadı');
    } finally {
      setUnlinkingKick(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-dark">
        <div className="text-white text-xl">Yükleniyor...</div>
      </div>
    );
  }

  if (!userData) {
    return null;
  }

  const roleDisplay = getRoleDisplay(userData.role || 'user');

  return (
    <div className="min-h-screen bg-gradient-dark py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-gradient-card rounded-xl p-8 border border-gold/30 shadow-2xl">
          <h1 className="text-3xl font-bold text-gold mb-8">Profilim</h1>

          <div className="space-y-6">
            {/* User Info Card */}
            <div className="bg-navy-800/50 rounded-lg p-6 border border-gold/20">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Kullanıcı Bilgileri</h2>
                {(userData.role === 'admin' || userData.role === 'moderator') && (
                  <div className={`px-4 py-2 rounded-lg border ${roleDisplay.bg} ${roleDisplay.border} ${roleDisplay.color} flex items-center gap-2`}>
                    <FaShieldAlt />
                    <span className="font-semibold">{roleDisplay.text}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-4 bg-navy-700/50 rounded-lg">
                  <FaUser className="text-gold text-xl" />
                  <div className="flex-1">
                    <div className="text-white/60 text-sm">Kullanıcı Adı</div>
                    <div className="text-white font-semibold">{userData.username}</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-navy-700/50 rounded-lg">
                  <FaEnvelope className="text-gold text-xl" />
                  <div className="flex-1">
                    <div className="text-white/60 text-sm mb-1">E-posta</div>
                    <div className="flex items-center justify-between">
                      <div className="text-white font-semibold">{userData.email}</div>
                      {emailVerified ? (
                        <div className="flex items-center gap-2 text-green-400">
                          <FaCheckCircle className="text-sm" />
                          <span className="text-xs font-semibold">Doğrulanmış</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-yellow-400">
                          <FaExclamationCircle className="text-sm" />
                          <span className="text-xs font-semibold">Doğrulanmamış</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-navy-700/50 rounded-lg">
                  <FaTelegram className="text-gold text-xl" />
                  <div className="flex-1">
                    <div className="text-white/60 text-sm mb-1">Telegram</div>
                    {isEditingTelegram ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={telegramUsername}
                          onChange={(e) => setTelegramUsername(e.target.value.replace('@', ''))}
                          placeholder="telegram_kullanici_adi"
                          className="flex-1 px-3 py-1.5 bg-navy-600 border border-gold/30 rounded text-white text-sm focus:outline-none focus:border-gold"
                          autoFocus
                        />
                        <button
                          onClick={handleUpdateTelegramUsername}
                          disabled={updating}
                          className="p-1.5 bg-green-500 hover:bg-green-600 text-white rounded transition-colors disabled:opacity-50"
                          title="Kaydet"
                        >
                          <FaSave className="text-sm" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={updating}
                          className="p-1.5 bg-red-500 hover:bg-red-600 text-white rounded transition-colors disabled:opacity-50"
                          title="İptal"
                        >
                          <FaTimes className="text-sm" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="text-white font-semibold">@{userData.telegramUsername || 'Belirtilmemiş'}</div>
                        <button
                          onClick={() => {
                            setIsEditingTelegram(true);
                            setTelegramUsername(userData.telegramUsername || '');
                          }}
                          className="p-1.5 bg-gold/20 hover:bg-gold/30 text-gold rounded transition-colors"
                          title="Düzenle"
                        >
                          <FaEdit className="text-xs" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-navy-700/50 rounded-lg">
                  <FaCoins className="text-gold text-xl" />
                  <div className="flex-1">
                    <div className="text-white/60 text-sm">Puan</div>
                    <div className="text-gold font-bold text-xl">{userData.coins || 0}</div>
                  </div>
                </div>
              </div>

              {/* Email Verification Notice */}
              {!emailVerified && (
                <div className="mt-4 p-4 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <p className="text-yellow-400 text-sm font-semibold mb-2">
                        ⚠️ E-posta Doğrulama Gerekli
                      </p>
                      <p className="text-white/80 text-sm mb-3">
                        E-posta adresinizi doğrulamadığınız için puan kodu kullanamaz ve oyun oynayamazsınız. 
                        Lütfen e-postanızı kontrol edin veya doğrulama e-postasını tekrar gönderin.
                      </p>
                      {emailMessage === 'success' && (
                        <p className="text-green-400 text-sm font-semibold mb-2">
                          ✓ Doğrulama e-postası başarıyla gönderildi! Lütfen e-postanızı kontrol edin.
                        </p>
                      )}
                      {emailMessage === 'error' && (
                        <p className="text-red-400 text-sm font-semibold mb-2">
                          ✗ E-posta gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
                        </p>
                      )}
                      <button
                        onClick={handleResendVerification}
                        disabled={resendingEmail}
                        className="bg-yellow-500 hover:bg-yellow-600 text-navy-900 font-bold px-4 py-2 rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
                      >
                        <FaPaperPlane className="text-sm" />
                        {resendingEmail ? 'Gönderiliyor...' : 'Doğrulama E-postası Gönder'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Telegram Username Update Notice */}
              {!userData.telegramUsername && (
                <div className="mt-4 p-3 bg-yellow-500/20 border border-yellow-500/30 rounded-lg">
                  <p className="text-yellow-400 text-sm">
                    <strong>Bilgi:</strong> Kullanıcı adınız değiştiyse lütfen güncelleyin.
                  </p>
                </div>
              )}
            </div>

            {/* Kick Integration */}
            <div className="bg-navy-800/50 rounded-lg p-6 border border-gold/20">
              <div className="flex items-center gap-3 mb-4">
                <SiKick className="text-gold text-xl" />
                <h2 className="text-xl font-bold text-white">Kick Entegrasyonu</h2>
              </div>

              {kickUsername ? (
                <div className="flex items-center justify-between flex-wrap gap-3 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                  <div className="flex items-center gap-2">
                    <FaCheckCircle className="text-green-400" />
                    <span className="text-white">
                      Kick hesabınız bağlı: <strong className="text-green-400">@{kickUsername}</strong>
                    </span>
                  </div>
                  <button
                    onClick={handleUnlinkKick}
                    disabled={unlinkingKick}
                    className="flex items-center gap-2 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <FaUnlink className="text-xs" />
                    {unlinkingKick ? 'Kaldırılıyor...' : 'Bağlantıyı Kaldır'}
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-white/70 text-sm mb-4">
                    Kick hesabınızı bağlayın; yayın sırasında chate yazarak etkinliklere katılın ve dağıtılan puanları doğrudan hesabınızda görün.
                  </p>

                  {kickLinkCode ? (
                    <div className="p-4 bg-navy-700/50 rounded-lg border border-gold/20">
                      <p className="text-white/80 text-sm mb-3">
                        Canlı yayın sırasında chate aşağıdaki komutu yazın:
                      </p>
                      <div className="flex items-center gap-2 mb-3">
                        <code className="flex-1 bg-black/40 text-gold font-bold text-lg px-4 py-2.5 rounded-lg text-center tracking-wider">
                          {kickLinkCode.command}
                        </code>
                        <button
                          onClick={handleCopyKickCommand}
                          className="p-2.5 bg-gold/20 hover:bg-gold/30 text-gold rounded-lg transition-colors"
                          title="Kopyala"
                        >
                          <FaCopy />
                        </button>
                      </div>
                      {kickCodeCopied && <p className="text-green-400 text-xs mb-2">✓ Kopyalandı</p>}
                      <p className="text-white/50 text-xs">
                        Kod 15 dakika geçerlidir. Süresi dolarsa yeni kod oluşturabilirsiniz.
                      </p>
                      <button
                        onClick={handleGenerateKickCode}
                        disabled={generatingKickCode}
                        className="mt-3 text-xs text-gold/80 hover:text-gold underline decoration-dotted"
                      >
                        Yeni kod oluştur
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleGenerateKickCode}
                      disabled={generatingKickCode}
                      className="flex items-center gap-2 bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-5 py-2.5 rounded-lg transition-all disabled:opacity-50"
                    >
                      <SiKick />
                      {generatingKickCode ? 'Oluşturuluyor...' : 'Kick Hesabını Bağla'}
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Role Info - Only for admin/moderator */}
            {(userData.role === 'admin' || userData.role === 'moderator') && (
              <div className="bg-navy-800/50 rounded-lg p-6 border border-gold/20">
                <h3 className="text-lg font-bold text-white mb-4">Yetki Bilgisi</h3>
                <div className="space-y-2">
                  {userData.role === 'admin' && (
                    <div className="text-white/80">
                      <strong className="text-gold">Admin:</strong> Tam yetkiye sahipsiniz. Tüm admin paneli özelliklerine erişebilirsiniz.
                    </div>
                  )}
                  {userData.role === 'moderator' && (
                    <div className="text-white/80">
                      <strong className="text-gold">Moderatör:</strong> Kısıtlı yetkiye sahipsiniz. Kullanıcıları görüntüleyebilir ve IP çakışmalarını kontrol edebilirsiniz.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white font-bold px-6 py-3 rounded-lg transition-all duration-300"
              >
                Çıkış Yap
              </button>
              {(userData.role === 'admin' || userData.role === 'moderator') && (
                <button
                  onClick={() => navigate('/admin')}
                  className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-6 py-3 rounded-lg transition-all duration-300 shadow-gold"
                >
                  {userData.role === 'admin' ? 'Admin Paneli' : 'Moderatör Paneli'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
